import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Plus, Trash2, TrendingUp, TrendingDown, X, AlertCircle, Check, Volume2, Activity, Clock, BellRing, BellOff, RefreshCw, Settings, Search, BarChart3 } from 'lucide-react';
import Card from '../components/Card';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import api, { Alert, CreateAlert, IndicatorType } from '../services/api';
import eodhd from '../services/eodhd';

type LocalAlertType = 'price' | 'rsi' | 'macd' | 'sma_cross' | 'momentum';

interface AlertWithPrice extends Alert {
  currentPrice?: number;
  priceChange?: number;
  rsi?: number;
  volume?: number;
}

interface ExtendedCreateAlert extends CreateAlert {
  localAlertType?: LocalAlertType;
  indicator_value?: number;
}

interface NotificationPrefs {
  browser: boolean;
  sound: boolean;
  autoRefresh: boolean;
}

const Alerts: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [alerts, setAlerts] = useState<AlertWithPrice[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<LocalAlertType | 'all'>('all');
  const [alertType, setAlertType] = useState<LocalAlertType>('price');
  const [newAlert, setNewAlert] = useState<ExtendedCreateAlert>({
    symbol: '',
    name: '',
    condition: 'above',
    target_price: 0,
    localAlertType: 'price',
    indicator_value: 30,
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [notificationPrefs, setNotificationPrefs] = useState<NotificationPrefs>(() => {
    const saved = localStorage.getItem('alertNotificationPrefs');
    return saved ? JSON.parse(saved) : { browser: true, sound: true, autoRefresh: true };
  });
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>('default');

  // Check notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  // Save notification prefs to localStorage
  useEffect(() => {
    localStorage.setItem('alertNotificationPrefs', JSON.stringify(notificationPrefs));
  }, [notificationPrefs]);

  // Auto-refresh alerts every 60 seconds if enabled
  useEffect(() => {
    if (!isAuthenticated || !notificationPrefs.autoRefresh) return;

    const interval = setInterval(() => {
      loadAlerts(true);
    }, 60000);

    return () => clearInterval(interval);
  }, [isAuthenticated, notificationPrefs.autoRefresh]);

  useEffect(() => {
    if (isAuthenticated) {
      loadAlerts();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      if (permission === 'granted') {
        setSuccessMessage('Browser notifications enabled!');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    }
  };

  const sendNotification = (title: string, body: string) => {
    if (notificationPrefs.browser && notificationPermission === 'granted') {
      new Notification(title, {
        body,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
      });
    }
    if (notificationPrefs.sound) {
      // Play notification sound
      const audio = new Audio('/notification.mp3');
      audio.volume = 0.5;
      audio.play().catch(() => {}); // Ignore if can't play
    }
  };

  const loadAlerts = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setRefreshing(true);
      const alertsData = await api.getAlerts();

      // Fetch current prices and additional data for each alert
      const alertsWithPrices = await Promise.all(
        alertsData.map(async (alert) => {
          try {
            const priceData = await eodhd.getRealTimePrice(alert.symbol);
            return {
              ...alert,
              currentPrice: priceData.close,
              priceChange: priceData.change_percent ?? priceData.change_p,
              volume: priceData.volume,
            };
          } catch {
            return alert;
          }
        })
      );

      // Check for newly triggered alerts
      const previousAlerts = alerts;
      alertsWithPrices.forEach((alert) => {
        const prevAlert = previousAlerts.find((a) => a.id === alert.id);
        if (prevAlert && !prevAlert.is_triggered && alert.is_triggered) {
          sendNotification(
            `🔔 Alert Triggered: ${alert.symbol}`,
            `${alert.name} has ${alert.condition === 'above' ? 'risen above' : 'fallen below'} $${alert.target_price}`
          );
        }
      });

      setAlerts(alertsWithPrices);
    } catch (err) {
      console.error('Error loading alerts:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [alerts, notificationPrefs, notificationPermission]);

  const handleCreateAlert = async () => {
    if (!newAlert.symbol) {
      setError('Please enter a symbol');
      return;
    }

    if (alertType === 'price' && !newAlert.target_price) {
      setError('Please enter a target price');
      return;
    }

    if (alertType !== 'price' && !newAlert.indicator_value) {
      setError('Please enter an indicator target value');
      return;
    }

    try {
      setError('');

      // Build the alert data based on alert type
      const alertData: CreateAlert = {
        symbol: newAlert.symbol,
        name: newAlert.name || newAlert.symbol.split('.')[0],
        condition: newAlert.condition,
        target_price: alertType === 'price' ? newAlert.target_price : 0,
      };

      // Add indicator-specific fields for non-price alerts
      if (alertType !== 'price') {
        alertData.alert_type = 'indicator';
        alertData.indicator_type = alertType as IndicatorType;
        alertData.indicator_target = newAlert.indicator_value;
      } else {
        alertData.alert_type = 'price';
      }

      await api.createAlert(alertData);
      setShowAddModal(false);
      setNewAlert({
        symbol: '',
        name: '',
        condition: 'above',
        target_price: 0,
        localAlertType: 'price',
        indicator_value: 30,
      });
      setAlertType('price');
      loadAlerts();
      setSuccessMessage('Alert created successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create alert');
    }
  };

  const handleDeleteAlert = async (id: number) => {
    try {
      await api.deleteAlert(id);
      setAlerts(alerts.filter((a) => a.id !== id));
      setSuccessMessage('Alert deleted');
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      console.error('Error deleting alert:', err);
    }
  };

  const handleToggleAlert = async (alert: AlertWithPrice) => {
    try {
      await api.updateAlert(alert.id, {
        symbol: alert.symbol,
        name: alert.name,
        condition: alert.condition,
        target_price: alert.target_price
      });
      loadAlerts();
    } catch (err) {
      console.error('Error toggling alert:', err);
    }
  };

  const getDistanceToTarget = (alert: AlertWithPrice) => {
    if (!alert.currentPrice) return null;
    const distance = ((alert.target_price - alert.currentPrice) / alert.currentPrice) * 100;
    return distance;
  };

  const getProgressToTarget = (alert: AlertWithPrice) => {
    if (!alert.currentPrice) return 0;
    const distance = Math.abs(alert.target_price - alert.currentPrice);
    const range = Math.abs(alert.target_price * 0.1); // 10% range for visualization
    return Math.min(100, Math.max(0, (1 - distance / range) * 100));
  };

  const filteredAlerts = alerts.filter((alert) => {
    const matchesSearch = !searchQuery ||
      alert.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
      alert.name.toLowerCase().includes(searchQuery.toLowerCase());

    let matchesType = filterType === 'all';
    if (!matchesType) {
      if (filterType === 'price') {
        matchesType = alert.alert_type === 'price' || !alert.alert_type;
      } else {
        matchesType = alert.alert_type === 'indicator' && alert.indicator_type === filterType;
      }
    }
    return matchesSearch && matchesType;
  });

  const activeAlerts = filteredAlerts.filter((a) => a.is_active && !a.is_triggered);
  const triggeredAlerts = filteredAlerts.filter((a) => a.is_triggered);

  if (!isAuthenticated) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <Bell size={48} className="mx-auto text-slate-600 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Sign in to view alerts</h2>
          <p className="text-slate-400">Create price alerts to get notified when assets hit your target prices</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 space-y-6">
      {/* Success Toast */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 bg-emerald-500/20 text-emerald-400 px-4 py-3 rounded-lg border border-emerald-500/30 animate-fade-in">
          <Check size={16} />
          {successMessage}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="text-teal-400" />
            Alerts
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Get notified when prices or technical indicators hit your targets
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => loadAlerts()}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={() => setShowSettingsModal(true)}
            className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
          >
            <Settings size={16} />
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-lg transition-colors"
          >
            <Plus size={16} />
            Create Alert
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-500/20 rounded-lg flex items-center justify-center">
              <Activity size={20} className="text-teal-400" />
            </div>
            <div>
              <p className="text-xs text-slate-400">Active</p>
              <p className="text-2xl font-bold font-mono text-teal-400">{activeAlerts.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <BellRing size={20} className="text-amber-400" />
            </div>
            <div>
              <p className="text-xs text-slate-400">Triggered</p>
              <p className="text-2xl font-bold font-mono text-amber-400">{triggeredAlerts.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Clock size={20} className="text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-slate-400">Total Alerts</p>
              <p className="text-2xl font-bold font-mono">{alerts.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              notificationPermission === 'granted' ? 'bg-emerald-500/20' : 'bg-slate-700'
            }`}>
              {notificationPermission === 'granted' ? (
                <Bell size={20} className="text-emerald-400" />
              ) : (
                <BellOff size={20} className="text-slate-400" />
              )}
            </div>
            <div>
              <p className="text-xs text-slate-400">Notifications</p>
              <p className="text-sm font-medium">
                {notificationPermission === 'granted' ? 'Enabled' : 'Disabled'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search alerts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
          />
        </div>
        <div className="flex gap-2">
          {(['all', 'price', 'rsi', 'macd', 'sma_cross', 'momentum'] as const).map((type) => (
            <button
              key={type}
              onClick={() => setFilterType(type)}
              className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                filterType === type
                  ? 'bg-teal-500 text-slate-900 font-semibold'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              {type === 'all' ? 'All' :
               type === 'price' ? 'Price' :
               type === 'rsi' ? 'RSI' :
               type === 'macd' ? 'MACD' :
               type === 'sma_cross' ? 'SMA Cross' : 'Momentum'}
            </button>
          ))}
        </div>
      </div>

      {/* Alerts List */}
      <div className="grid gap-6">
        {/* Active Alerts */}
        <Card title="Active Alerts" icon="🟢">
          {loading ? (
            <LoadingSpinner />
          ) : activeAlerts.length === 0 ? (
            <div className="text-center py-8">
              <Activity size={36} className="mx-auto text-slate-600 mb-3" />
              <p className="text-slate-400">No active alerts</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="mt-3 text-teal-400 hover:text-teal-300 text-sm"
              >
                + Create your first alert
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {activeAlerts.map((alert) => {
                const distance = getDistanceToTarget(alert);
                const progress = getProgressToTarget(alert);

                return (
                  <div
                    key={alert.id}
                    className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg border border-slate-700/50 hover:border-slate-600 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-slate-700 flex items-center justify-center">
                        {alert.alert_type === 'indicator' ? (
                          <BarChart3 size={24} className="text-purple-400" />
                        ) : alert.condition === 'above' ? (
                          <TrendingUp size={24} className="text-emerald-400" />
                        ) : (
                          <TrendingDown size={24} className="text-red-400" />
                        )}
                      </div>

                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-lg text-teal-400">
                            {alert.symbol.split('.')[0]}
                          </span>
                          <span className="text-sm text-slate-400">{alert.name}</span>
                          {alert.alert_type === 'indicator' && (
                            <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">
                              {alert.indicator_type?.toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-400 mt-1">
                          {alert.alert_type === 'indicator' ? (
                            <>
                              <span>
                                Alert when {alert.indicator_type?.toUpperCase()} goes{' '}
                                <span className={alert.condition === 'above' ? 'text-emerald-400' : 'text-red-400'}>
                                  {alert.condition}
                                </span>
                              </span>
                              <span className="font-mono font-semibold text-white">
                                {alert.indicator_target}
                              </span>
                            </>
                          ) : (
                            <>
                              <span>
                                Alert when price goes{' '}
                                <span className={alert.condition === 'above' ? 'text-emerald-400' : 'text-red-400'}>
                                  {alert.condition}
                                </span>
                              </span>
                              <span className="font-mono font-semibold text-white">
                                ${alert.target_price.toFixed(2)}
                              </span>
                            </>
                          )}
                        </div>
                        {/* Progress bar - only for price alerts */}
                        {alert.alert_type !== 'indicator' && (
                          <div className="mt-2 w-48 h-1.5 bg-slate-600 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${
                                alert.condition === 'above' ? 'bg-emerald-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      {alert.currentPrice && (
                        <div className="text-right">
                          <div className="text-xs text-slate-400 mb-1">Current Price</div>
                          <div className="font-mono text-lg font-semibold">${alert.currentPrice.toFixed(2)}</div>
                          {distance !== null && (
                            <div
                              className={`text-xs font-medium ${
                                Math.abs(distance) < 5 ? 'text-amber-400' : 'text-slate-500'
                              }`}
                            >
                              {distance > 0 ? '+' : ''}
                              {distance.toFixed(1)}% to target
                            </div>
                          )}
                        </div>
                      )}

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleAlert(alert)}
                          className="p-2 hover:bg-slate-600 rounded-lg transition-colors"
                          title="Toggle alert"
                        >
                          {alert.is_active ? (
                            <Bell size={16} className="text-teal-400" />
                          ) : (
                            <BellOff size={16} className="text-slate-500" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteAlert(alert.id)}
                          className="p-2 hover:bg-red-500/20 hover:text-red-400 rounded-lg transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Triggered Alerts */}
        {triggeredAlerts.length > 0 && (
          <Card title="Triggered Alerts" icon="🔔">
            <div className="space-y-3">
              {triggeredAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className="flex items-center justify-between p-4 bg-amber-500/10 rounded-lg border border-amber-500/30"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                      <BellRing size={24} className="text-amber-400" />
                    </div>

                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-lg text-teal-400">
                          {alert.symbol.split('.')[0]}
                        </span>
                        <span className="text-sm text-slate-400">{alert.name}</span>
                        {alert.alert_type === 'indicator' && (
                          <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">
                            {alert.indicator_type?.toUpperCase()}
                          </span>
                        )}
                        <span className="flex items-center gap-1 text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full">
                          <Check size={12} />
                          Triggered
                        </span>
                      </div>
                      <div className="text-sm text-slate-400 mt-1">
                        {alert.alert_type === 'indicator' ? (
                          <>
                            {alert.indicator_type?.toUpperCase()} went {alert.condition}{' '}
                            <span className="font-mono font-semibold text-amber-400">
                              {alert.indicator_target}
                            </span>
                            {alert.triggered_indicator_value && (
                              <span className="ml-1 text-slate-500">
                                (was {alert.triggered_indicator_value})
                              </span>
                            )}
                          </>
                        ) : (
                          <>
                            Price went {alert.condition}{' '}
                            <span className="font-mono font-semibold text-amber-400">
                              ${alert.target_price.toFixed(2)}
                            </span>
                            {alert.triggered_price && (
                              <span className="ml-1 text-slate-500">
                                (at ${alert.triggered_price})
                              </span>
                            )}
                          </>
                        )}
                        {alert.triggered_at && (
                          <span className="ml-2 text-xs text-slate-500">
                            • {new Date(alert.triggered_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {alert.currentPrice && (
                      <div className="text-right">
                        <div className="text-xs text-slate-400 mb-1">Current</div>
                        <div className="font-mono text-lg font-semibold">${alert.currentPrice.toFixed(2)}</div>
                      </div>
                    )}
                    <button
                      onClick={() => handleDeleteAlert(alert.id)}
                      className="p-2 hover:bg-red-500/20 hover:text-red-400 rounded-lg transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Add Alert Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-[480px] mx-4 sm:mx-0 border border-slate-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">Create Alert</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-slate-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 bg-red-400/10 px-4 py-3 rounded-lg text-sm mb-4">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            {/* Alert Type Selection */}
            <div className="mb-6">
              <label className="text-sm text-slate-400 block mb-2">Alert Type</label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {([
                  { type: 'price', icon: TrendingUp, label: 'Price' },
                  { type: 'rsi', icon: Activity, label: 'RSI' },
                  { type: 'macd', icon: BarChart3, label: 'MACD' },
                  { type: 'sma_cross', icon: TrendingUp, label: 'SMA' },
                  { type: 'momentum', icon: Activity, label: 'Momentum' },
                ] as const).map(({ type, icon: Icon, label }) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setAlertType(type as LocalAlertType)}
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-colors ${
                      alertType === type
                        ? 'border-teal-500 bg-teal-500/20 text-teal-400'
                        : 'border-slate-600 hover:border-slate-500'
                    }`}
                  >
                    <Icon size={20} />
                    <span className="text-xs">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-400 block mb-1">Symbol</label>
                <input
                  type="text"
                  placeholder="e.g., AAPL.US, NPN.JSE, BTC-USD.CC"
                  value={newAlert.symbol}
                  onChange={(e) => setNewAlert({ ...newAlert, symbol: e.target.value.toUpperCase() })}
                  className="w-full bg-slate-700 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div>
                <label className="text-sm text-slate-400 block mb-1">Name (optional)</label>
                <input
                  type="text"
                  placeholder="e.g., Apple Inc"
                  value={newAlert.name}
                  onChange={(e) => setNewAlert({ ...newAlert, name: e.target.value })}
                  className="w-full bg-slate-700 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div>
                <label className="text-sm text-slate-400 block mb-1">Condition</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setNewAlert({ ...newAlert, condition: 'above' })}
                    className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-colors ${
                      newAlert.condition === 'above'
                        ? 'border-emerald-500 bg-emerald-500/20 text-emerald-400'
                        : 'border-slate-600 hover:border-slate-500'
                    }`}
                  >
                    <TrendingUp size={16} />
                    {alertType === 'rsi' ? 'Above (Overbought)' : 'Goes Above'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewAlert({ ...newAlert, condition: 'below' })}
                    className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-colors ${
                      newAlert.condition === 'below'
                        ? 'border-red-500 bg-red-500/20 text-red-400'
                        : 'border-slate-600 hover:border-slate-500'
                    }`}
                  >
                    <TrendingDown size={16} />
                    {alertType === 'rsi' ? 'Below (Oversold)' : 'Goes Below'}
                  </button>
                </div>
              </div>

              {/* Dynamic input based on alert type */}
              {alertType === 'price' && (
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Target Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="150.00"
                    value={newAlert.target_price || ''}
                    onChange={(e) =>
                      setNewAlert({ ...newAlert, target_price: parseFloat(e.target.value) || 0 })
                    }
                    className="w-full bg-slate-700 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              )}

              {alertType === 'rsi' && (
                <div>
                  <label className="text-sm text-slate-400 block mb-1">RSI Value (0-100)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    placeholder={newAlert.condition === 'below' ? '30' : '70'}
                    value={newAlert.indicator_value || ''}
                    onChange={(e) =>
                      setNewAlert({ ...newAlert, indicator_value: parseFloat(e.target.value) || 0 })
                    }
                    className="w-full bg-slate-700 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {newAlert.condition === 'below'
                      ? 'RSI below 30 typically indicates oversold conditions'
                      : 'RSI above 70 typically indicates overbought conditions'}
                  </p>
                </div>
              )}

              {alertType === 'macd' && (
                <div>
                  <label className="text-sm text-slate-400 block mb-1">MACD Value</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0"
                    value={newAlert.indicator_value || ''}
                    onChange={(e) =>
                      setNewAlert({ ...newAlert, indicator_value: parseFloat(e.target.value) || 0 })
                    }
                    className="w-full bg-slate-700 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {newAlert.condition === 'above'
                      ? 'MACD above 0 indicates bullish momentum'
                      : 'MACD below 0 indicates bearish momentum'}
                  </p>
                </div>
              )}

              {alertType === 'sma_cross' && (
                <div>
                  <label className="text-sm text-slate-400 block mb-1">SMA Period Ratio</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="1.0"
                    value={newAlert.indicator_value || ''}
                    onChange={(e) =>
                      setNewAlert({ ...newAlert, indicator_value: parseFloat(e.target.value) || 0 })
                    }
                    className="w-full bg-slate-700 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {newAlert.condition === 'above'
                      ? 'Alert when short-term SMA crosses above long-term (Golden Cross)'
                      : 'Alert when short-term SMA crosses below long-term (Death Cross)'}
                  </p>
                </div>
              )}

              {alertType === 'momentum' && (
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Momentum Value</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="0"
                    value={newAlert.indicator_value || ''}
                    onChange={(e) =>
                      setNewAlert({ ...newAlert, indicator_value: parseFloat(e.target.value) || 0 })
                    }
                    className="w-full bg-slate-700 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {newAlert.condition === 'above'
                      ? 'Positive momentum indicates upward price trend'
                      : 'Negative momentum indicates downward price trend'}
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAlert}
                className="flex-1 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-lg transition-colors"
              >
                Create Alert
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-sm mx-4 sm:mx-0 border border-slate-700">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">Alert Settings</h3>
              <button
                onClick={() => setShowSettingsModal(false)}
                className="text-slate-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Browser Notifications */}
              <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <Bell size={20} className="text-teal-400" />
                  <div>
                    <p className="font-medium">Browser Notifications</p>
                    <p className="text-xs text-slate-400">Show desktop alerts</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (notificationPermission !== 'granted') {
                      requestNotificationPermission();
                    } else {
                      setNotificationPrefs({ ...notificationPrefs, browser: !notificationPrefs.browser });
                    }
                  }}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    notificationPrefs.browser && notificationPermission === 'granted'
                      ? 'bg-teal-500'
                      : 'bg-slate-600'
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full transition-transform ${
                      notificationPrefs.browser && notificationPermission === 'granted'
                        ? 'translate-x-6'
                        : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>

              {/* Sound Notifications */}
              <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <Volume2 size={20} className="text-purple-400" />
                  <div>
                    <p className="font-medium">Sound Alerts</p>
                    <p className="text-xs text-slate-400">Play sound on trigger</p>
                  </div>
                </div>
                <button
                  onClick={() => setNotificationPrefs({ ...notificationPrefs, sound: !notificationPrefs.sound })}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    notificationPrefs.sound ? 'bg-purple-500' : 'bg-slate-600'
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full transition-transform ${
                      notificationPrefs.sound ? 'translate-x-6' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>

              {/* Auto Refresh */}
              <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <RefreshCw size={20} className="text-amber-400" />
                  <div>
                    <p className="font-medium">Auto Refresh</p>
                    <p className="text-xs text-slate-400">Check alerts every 60s</p>
                  </div>
                </div>
                <button
                  onClick={() => setNotificationPrefs({ ...notificationPrefs, autoRefresh: !notificationPrefs.autoRefresh })}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    notificationPrefs.autoRefresh ? 'bg-amber-500' : 'bg-slate-600'
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full transition-transform ${
                      notificationPrefs.autoRefresh ? 'translate-x-6' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>

              {notificationPermission === 'denied' && (
                <div className="flex items-center gap-2 text-amber-400 bg-amber-400/10 px-3 py-2 rounded-lg text-sm">
                  <AlertCircle size={16} />
                  <span>Browser notifications blocked. Enable in browser settings.</span>
                </div>
              )}
            </div>

            <button
              onClick={() => setShowSettingsModal(false)}
              className="w-full mt-6 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Alerts;
